<template>
  <button
    role="button"
    :id="dayInDate.format(dayFormat)"
    class="calendar-arrow-day"
    :class="[
      { 'is-start-date': isSelected },
      { error: errorStatus },
    ]"
    :style="[
      { 'margin-left': marginLeft },
      { height: dayHeight + 'px' },
    ]"
    :disabled="isDisabled"
    :aria-disabled="isDisabled"
    :tabindex="isSelected || isStartDate || isCurrentFocus ? '0' : '-1'"
    :aria-label="ariaLabel"
    @focus="bindKeyboardEvents"
    @blur="unbindKeyboardEvents"
    @click.prevent="updateField"
    @keydown.space.prevent="updateField"
  >
    <div class="calendar-arrow-day-selected" />
    <span class="calendar-arrow-day-label">
      {{ dayOfMonth }}
    </span>
  </button>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { getDate, formatDateZone } from '@utils/date-helpers'
import { KEY_CODE_MAP } from '@utils/keyboard-helpers'

export default {
  name: 'calendar-input-day',
  props: {
    day: {
      type: String,
      required: true
    },
    selectedDate: {
      type: String
    },
    currentFocus: {
      type: String
    },
    isDayDisabled: Boolean,
    isFirstDay: Boolean
  },
  inject: [
    'selectDate',
    'startDate',
    'endDate',
    'disabledDates',
    'disabledWeekDates'
  ],
  data: () => ({
    dayHeight: 80
  }),
  computed: {
    ...mapState('booking', ['now']),
    ...mapGetters('booking', ['dayFormat']),
    ...mapGetters('i18n', ['tc']),
    ariaDateFormat() {
      return 'dddd[,] D MMMM YYYY'
    },
    ariaLabel() {
      return formatDateZone(this.day, null, this.ariaDateFormat)
    },
    dayInDate() {
      return this.getDay(this.day)
    },
    dayOfMonth() {
      return this.dayInDate.date()
    },
    dayOfWeek() {
      return this.dayInDate.day()
    },
    isStartDate() {
      return this.isStartMonth ? this.getDay(this.startDate).isSame(this.day, 'day') : this.isFirstDay
    },
    isStartMonth() {
      return this.getDay(this.startDate).isSame(this.day, 'month')
    },
    isEndDate() {
      return this.endDate && this.getDay(this.endDate).isSame(this.day, 'day')
    },
    isSelected() {
      return this.getDay(this.selectedDate).isSame(this.day, 'day')
    },
    isCurrentFocus() {
      return this.currentFocus === this.dayInDate.format(this.dayFormat)
    },
    errorStatus() {
      return this.isDisabled && this.isSelected
    },
    isDisabled() {
      return this.getDay(this.startDate).isAfter(this.dayInDate, 'day') || (this.endDate && this.getDay(this.endDate).isBefore(this.dayInDate, 'day')) || this.isDayDisabled
    },
    marginLeft() {
      return this.dayOfMonth === 1 ? (this.dayOfWeek * 14.28) + '%' : 0
    },
  },
  mounted() {
    this.dayHeight = this.$el.getBoundingClientRect().width
  },
  methods: {
    getDay(day) {
      return getDate(day, this.dayFormat)
    },
    async updateField() {
      this.selectDate(this.dayInDate.format(this.dayFormat))
    },
    bindKeyboardEvents() {
      this.$el.addEventListener('keydown', this.onKeydownEvent)
    },
    unbindKeyboardEvents() {
      this.$el.removeEventListener('keydown', this.onKeydownEvent)
    },
    onKeydownEvent(e) {
      let elementId = null
      const date = getDate(this.day)
      if (e.keyCode === KEY_CODE_MAP.ARROW_RIGHT) {
        date.add(1, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.keyCode === KEY_CODE_MAP.ARROW_LEFT) {
        date.subtract(1, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.keyCode === KEY_CODE_MAP.ARROW_UP) {
        date.subtract(7, 'day')
        elementId = date.format(this.dayFormat)
      } else if (e.keyCode === KEY_CODE_MAP.ARROW_DOWN) {
        date.add(7, 'day')
        elementId = date.format(this.dayFormat)
      }
      if (elementId) {
        e.stopPropagation()
        e.preventDefault()
        document.getElementById(elementId).focus()
        this.$emit('refresh-focusable', elementId)
      }
    }
  }
}
</script>
